/**
 * Persistence for the active patient context.
 *
 * sessionStorage (not localStorage) is deliberate: it is per-tab, dies with the
 * tab, and is already wiped by the existing `sessionStorage.clear()` on logout.
 * Two tabs therefore hold two independent patient contexts, which is what a
 * user comparing patients expects.
 *
 * Only the identity fields the header strip renders are stored. Reports, FHIR
 * bundles and history are never persisted — they are re-fetched from patient_id.
 */

const KEY = "activePatientContext";

// A patient left selected overnight is a mis-filing risk: treat it as stale.
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

const read = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch (err) {
    console.error("Failed to read active patient context:", err);
    return null;
  }
};

const write = (patient, encounter) => {
  try {
    if (!patient) {
      sessionStorage.removeItem(KEY);
      return;
    }
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ patient, encounter: encounter ?? null, savedAt: Date.now() }),
    );
  } catch (err) {
    console.error("Failed to persist active patient context:", err);
  }
};

export const readPersistedContext = () => {
  const stored = read();
  return {
    patient: stored?.patient ?? null,
    encounter: stored?.encounter ?? null,
  };
};

export const persistContext = (patient, encounter) => write(patient, encounter);

export const clearPersistedContext = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (err) {
    console.error("Failed to clear active patient context:", err);
  }
};
